export interface ForeplayUsage {
  spendingReady: boolean;
  spendingBlockReason?: string | null;
  totalCredits: number;
  remainingCredits: number;
  observedAt: string;
  expiresAt: string;
  verified: boolean;
  reservedUnits: number;
  availableCredits: number;
}

export interface ForeplayCandidate {
  id: number;
  brandName: string;
  niche?: string | null;
  classification: "relevant" | "irrelevant" | "uncertain" | null;
  reason?: string | null;
  approved: boolean;
  scanStatus: string;
  scanError?: string | null;
  adsScraped?: number | null;
}

type ForeplayCandidateResponse = Omit<Partial<ForeplayCandidate>, "classification"> & {
  id: number;
  brandName: string;
  classification?: ForeplayCandidate["classification"];
  foreplayClassification?: ForeplayCandidate["classification"] | "unreviewed";
  foreplayClassificationReason?: string | null;
};

export interface ForeplayJob {
  id: number;
  status: string;
  error?: string | null;
  createdAt: string;
  updatedAt: string;
  foreplayCheckpoint?: unknown;
}

export interface ForeplayJobDetail {
  job: ForeplayJob;
  candidates: ForeplayCandidate[];
}

export interface ForeplayCoverage {
  approvedCount: number;
  diversity: { creativeCount: number; brandCount: number; angleCount: number; styleCount: number };
  ads: Array<{ id: number; sourceBrand: string; niche: string | null; imageUrl: string | null; daysLive: number | null; foreplayAdId: string | null; sourceUrl: string | null; analysisStatus: string }>;
  page: { limit: number; nextAfterId: number | null; hasMore: boolean };
}

export interface ForeplayJobCreateInput {
  requestKey: string;
  brands: Array<{ foreplayId: string; name: string; niche?: string | null; metaPageId?: string | null }>;
  filters: { displayFormat?: string; live?: boolean; minDaysRunning?: number; order?: string; capPerBrand: number };
}

export function isForeplayUsageUsable(usage: ForeplayUsage | null | undefined, now = Date.now()): boolean {
  if (!usage || usage.verified !== true || usage.spendingReady !== true || !Number.isFinite(usage.availableCredits) || usage.availableCredits <= 0) return false;
  const expiresAt = Date.parse(usage.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > now;
}

export function canStartForeplayJob(candidates: ForeplayCandidate[]): boolean {
  return candidates.some((candidate) => candidate.classification === "relevant" && candidate.approved);
}

export function createRequestKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `foreplay-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function requestJson<T>(fetcher: typeof fetch, path: string, init?: RequestInit): Promise<T> {
  const response = await fetcher(path, init);
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { message?: string } | null;
    throw new Error(body?.message || `Request failed (HTTP ${response.status})`);
  }
  return response.json() as Promise<T>;
}

function normalizeCandidate(candidate: ForeplayCandidateResponse): ForeplayCandidate {
  const storedClassification = candidate.foreplayClassification;
  const classification = candidate.classification ?? (storedClassification === "unreviewed" ? null : storedClassification ?? null);
  return {
    ...candidate,
    classification,
    reason: candidate.reason ?? candidate.foreplayClassificationReason ?? null,
    approved: candidate.approved === true,
  } as ForeplayCandidate;
}

export function normalizeForeplayJobStatus(status: string): string {
  return status.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function canEditForeplayReview(status: string): boolean {
  return !["queued", "running", "discovering", "scanning", "cancelling"].includes(normalizeForeplayJobStatus(status));
}

function normalizeJob(job: ForeplayJob): ForeplayJob {
  return { ...job, status: normalizeForeplayJobStatus(job.status) };
}

function normalizeJobDetail(detail: Omit<Partial<ForeplayJobDetail>, "candidates"> & {
  job: ForeplayJob;
  candidates: ForeplayCandidateResponse[];
}): ForeplayJobDetail {
  return { ...detail, job: normalizeJob(detail.job), candidates: detail.candidates.map(normalizeCandidate) };
}

export function createForeplayExpansionApi(fetcher: typeof fetch = fetch) {
  const base = "/api/admin/foreplay-harvest";
  const jobDetail = async (path: string, init?: RequestInit) => normalizeJobDetail(
    await requestJson<Parameters<typeof normalizeJobDetail>[0]>(fetcher, `${base}${path}`, init),
  );
  const post = (path: string) => requestJson<{ jobId: number; status: string }>(fetcher, `${base}${path}`, { method: "POST" });
  return {
    discover: (niche: string, target: 10 | 20, officialWebsites?: string[]) => requestJson<{
      brands: Array<{ id: number; foreplayId: string; name: string; avatarUrl: string | null; category: string | null; niches: string[] | null; websites: string[] | null; metaPageId: string | null; lastAnalytics: { activeCount: number; image: number; video: number; carousel: number } | null }>;
      niche: string; target: number; partial: boolean; message: string | null; lookups: number; errors: string[];
      candidates: Array<{ domain: string; status: string; message?: string; evidence: Array<{ title: string; url: string; snippet: string }> }>;
    }>(fetcher, base + "/brands/discover", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ niche, target, officialWebsites }) }),
    usage: () => requestJson<ForeplayUsage>(fetcher, `${base}/usage`),
    jobs: async () => {
      const response = await requestJson<{ jobs: ForeplayJob[] }>(fetcher, `${base}/jobs`);
      if (!Array.isArray(response.jobs)) throw new Error("Invalid Foreplay job list response");
      return response.jobs.map(normalizeJob);
    },
    job: (jobId: number) => jobDetail(`/jobs/${jobId}`),
    create: (input: ForeplayJobCreateInput) => jobDetail("/jobs", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input),
    }),
    review: async (jobId: number, candidateId: number, input: {
      classification?: "relevant" | "irrelevant" | "uncertain"; reason?: string | null; approved?: boolean;
    }) => {
      const response = await requestJson<{ candidate: ForeplayCandidateResponse }>(fetcher, `${base}/jobs/${jobId}/candidates/${candidateId}/review`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input),
      });
      // Use the committed PATCH row to update the review cache.
      const candidate = normalizeCandidate(response.candidate);
      if (candidate.id !== candidateId) throw new Error("Review response did not match the selected candidate");
      const matches = (input.classification === undefined || candidate.classification === input.classification)
        && (input.reason === undefined || candidate.reason === input.reason)
        && (input.approved === undefined || candidate.approved === input.approved);
      if (!matches) throw new Error("Review was not saved. Reload the batch and try again.");
      return { candidate };
    },
    start: (jobId: number) => post(`/jobs/${jobId}/start`),
    resume: (jobId: number) => post(`/jobs/${jobId}/resume`),
    cancel: (jobId: number) => post(`/jobs/${jobId}/cancel`),
    coverage: (niche?: string, afterId?: number) => {
      const params = new URLSearchParams({ limit: "200" });
      if (niche) params.set("niche", niche);
      if (afterId != null) params.set("afterId", String(afterId));
      return requestJson<ForeplayCoverage>(fetcher, `${base}/coverage?${params.toString()}`);
    },
  };
}
